import { HttpService } from "./https.service";
import { BASE_URL } from "../../config/config";

const http = new HttpService();

export class StorefrontService {

    static async setupStorefront(data: any): Promise<any> {
        try {
            return await http.postRequest(BASE_URL + "/storefront/setup", data, null);
        } catch (error) { throw new Error(`setupStorefront: ${error}`); }
    }

    static async activateStorefront(data: { brand_id: number }): Promise<any> {
        try {
            return await http.postRequest(BASE_URL + "/storefront/activate", data, null);
        } catch (error) { throw new Error(`activateStorefront: ${error}`); }
    }

    static async getMyStorefront(brand_id: number): Promise<any> {
        try {
            return await http.getRequest(BASE_URL + `/storefront/my?brand_id=${brand_id}`, null);
        } catch (error) { throw new Error(`getMyStorefront: ${error}`); }
    }

    static async checkSlug(slug: string, brand_id: number): Promise<any> {
        try {
            return await http.getRequest(BASE_URL + `/storefront/slug-check?slug=${slug}&brand_id=${brand_id}`, null);
        } catch (error) { throw new Error(`checkSlug: ${error}`); }
    }

    static async getMyProducts(params: { storefront_id: number; page?: number; limit?: number; category?: string; status?: string; search?: string }): Promise<any> {
        try {
            const query = new URLSearchParams(params as any).toString();
            return await http.getRequest(BASE_URL + `/storefront/products?${query}`, null);
        } catch (error) { throw new Error(`getMyProducts: ${error}`); }
    }

    static async createProduct(data: any): Promise<any> {
        try {
            return await http.postRequest(BASE_URL + "/storefront/products", data, null);
        } catch (error) { throw new Error(`createProduct: ${error}`); }
    }

    static async updateProduct(data: any): Promise<any> {
        try {
            return await http.postRequest(BASE_URL + "/storefront/products/update", data, null);
        } catch (error) { throw new Error(`updateProduct: ${error}`); }
    }

    static async archiveProduct(product_id: number): Promise<any> {
        try {
            return await http.postRequest(BASE_URL + "/storefront/products/archive", { product_id }, null);
        } catch (error) { throw new Error(`archiveProduct: ${error}`); }
    }

    static async bulkUpload(formData: FormData): Promise<any> {
        try {
            return await http.postRequest(BASE_URL + "/storefront/products/bulk", formData, null);
        } catch (error) { throw new Error(`bulkUpload: ${error}`); }
    }

    static async generateAffiliateLink(data: { product_id: number; influencer_id: number; account_id: number }): Promise<any> {
        try {
            return await http.postRequest(BASE_URL + "/storefront/products/affiliate-link", data, null);
        } catch (error) { throw new Error(`generateAffiliateLink: ${error}`); }
    }

    // Requests to associate this storefront with one of the brand's own CashKaro/Trackier
    // "Affiliate Deals" campaigns — does NOT link it immediately. Trackier has no
    // advertiser/ownership field, so this only files a row in
    // cashkaro_campaign_link_requests; the live link (cashkaro_campaign_id on the
    // storefront row) is written only once an admin approves it. Check
    // storefrontRow.latest_campaign_request for the request's current status.
    static async requestCashkaroCampaignLink(data: { storefront_id: number; brand_id: number; cashkaro_campaign_id: number }): Promise<any> {
        try {
            return await http.postRequest(BASE_URL + "/storefront/cashkaro-link-request", data, null);
        } catch (error) { throw new Error(`requestCashkaroCampaignLink: ${error}`); }
    }

    // Instant, self-serve — unlike linking, there's no "wrong campaign" risk to guard
    // against here, so this needs no admin review.
    static async unlinkCashkaroCampaign(data: { storefront_id: number; brand_id: number }): Promise<any> {
        try {
            return await http.postRequest(BASE_URL + "/storefront/cashkaro-unlink", data, null);
        } catch (error) { throw new Error(`unlinkCashkaroCampaign: ${error}`); }
    }

    // Admin-only (view_publisher_manager-gated server-side) — lists every brand storefront
    // so an admin can flip the manual Affiliate Deals access flag per brand.
    static async adminGetStorefronts(user_id: number, search?: string): Promise<any> {
        try {
            const query = new URLSearchParams({ user_id: String(user_id), ...(search ? { search } : {}) }).toString();
            return await http.getRequest(BASE_URL + `/storefront/admin/storefronts?${query}`, null);
        } catch (error) { throw new Error(`adminGetStorefronts: ${error}`); }
    }

    static async adminSetBrandDealsEnabled(user_id: number, storefront_id: number, enabled: boolean): Promise<any> {
        try {
            return await http.postRequest(BASE_URL + "/storefront/admin/brand-deals-enabled", { user_id, storefront_id, enabled }, null);
        } catch (error) { throw new Error(`adminSetBrandDealsEnabled: ${error}`); }
    }

    // Brand's own Trackier performance for their linked CashKaro Affiliate Deals campaign —
    // clicks/payout trend, which influencers are promoting them, which products convert.
    static async getCashkaroPerformance(params: { brand_id: number; start?: string; end?: string; group_by?: string }): Promise<any> {
        try {
            const query = new URLSearchParams(
                Object.entries(params).reduce((acc: Record<string, string>, [key, value]) => {
                    if (value !== undefined && value !== null && value !== "") acc[key] = String(value);
                    return acc;
                }, {})
            ).toString();
            return await http.getRequest(BASE_URL + `/storefront/cashkaro-performance?${query}`, null);
        } catch (error) { throw new Error(`getCashkaroPerformance: ${error}`); }
    }

    // NEW — opts a single product in/out of the storefront's CURRENTLY linked CashKaro
    // campaign (see linkCashkaroCampaign above). Scoped per campaign_id, so switching to
    // a different campaign starts the selection fresh rather than carrying products over.
    static async toggleCampaignProduct(data: { storefront_id: number; brand_id: number; product_id: number; featured: boolean }): Promise<any> {
        try {
            return await http.postRequest(BASE_URL + "/storefront/campaign-products/toggle", data, null);
        } catch (error) { throw new Error(`toggleCampaignProduct: ${error}`); }
    }

    static async getPublicStorefront(slug: string): Promise<any> {
        try {
            return await http.getRequest(BASE_URL + `/storefront/view/${slug}`, null);
        } catch (error) { throw new Error(`getPublicStorefront: ${error}`); }
    }

    static async getPublicProduct(product_id: number): Promise<any> {
        try {
            return await http.getRequest(BASE_URL + `/storefront/view/product/${product_id}`, null);
        } catch (error) { throw new Error(`getPublicProduct: ${error}`); }
    }

    // Brand-facing attributed sales / commission ledger
    static async getConversions(params: { brand_id: number; status?: string; start_date?: string; end_date?: string; page?: number; limit?: number }): Promise<any> {
        try {
            const query = new URLSearchParams(
                Object.fromEntries(
                    Object.entries(params)
                        .filter(([, v]) => v !== undefined && v !== '')
                        .map(([k, v]) => [k, String(v)])
                )
            ).toString();
            return await http.getRequest(BASE_URL + `/storefront/conversions?${query}`, null);
        } catch (error) { throw new Error(`getConversions: ${error}`); }
    }

    static async updateConversionStatus(data: { brand_id: number; conversion_id: number; status: 'pending' | 'approved' | 'paid' | 'reversed' }): Promise<any> {
        try {
            return await http.postRequest(BASE_URL + "/storefront/conversions/status", data, null);
        } catch (error) { throw new Error(`updateConversionStatus: ${error}`); }
    }

    // Storefront traffic analytics (views/clicks) — works even without the pixel installed.
    static async getTrafficAnalytics(params: { brand_id: number; start_date?: string; end_date?: string }): Promise<any> {
        try {
            const query = new URLSearchParams(
                Object.fromEntries(Object.entries(params).filter(([, v]) => v !== undefined && v !== '').map(([k, v]) => [k, String(v)]))
            ).toString();
            return await http.getRequest(BASE_URL + `/storefront/traffic-analytics?${query}`, null);
        } catch (error) { throw new Error(`getTrafficAnalytics: ${error}`); }
    }

    // Shared public-page tracking (same endpoints as the influencer storefront).
    static async trackView(data: { slug: string; visitor_id: string; session_id: string; product_id?: number }): Promise<any> {
        try { return await http.postRequest(BASE_URL + "/storefront/track-view", data, null); } catch (error) { return null; }
    }
    static clickUrl(click_token: string): string {
        return `${BASE_URL}/storefront/r/${click_token}`;
    }
}
